/**
 * ToastProvider Component (T050)
 *
 * Provides toast notification context and renders the Toaster component.
 * Wraps @yak/ui's ToastProvider with our state management.
 */

'use client';

import { ToastProvider as RadixToastProvider } from '@yak/ui';
import { type ReactNode } from 'react';

import { ToastContext, useToastState } from '~/hooks/useToast';

import { Toaster } from './Toaster';

interface ToastProviderProps {
  /**
   * Child components that can access toast functionality
   */
  children: ReactNode;
  /**
   * Duration for toasts to auto-dismiss (default: 5000ms)
   */
  duration?: number;
  /**
   * Swipe direction for dismissing toasts
   */
  swipeDirection?: 'right' | 'left' | 'up' | 'down';
  /**
   * Threshold for swipe gesture to dismiss
   */
  swipeThreshold?: number;
}

/**
 * Toast provider that enables toast notifications throughout the app.
 *
 * @example
 * // In your root layout:
 * import { AppToastProvider } from '~/components/ToastProvider';
 *
 * function RootLayout({ children }) {
 *   return (
 *     <AppToastProvider>
 *       {children}
 *     </AppToastProvider>
 *   );
 * }
 *
 * // In any component:
 * import { useToast } from '~/hooks/useToast';
 *
 * function MyComponent() {
 *   const { toast } = useToast();
 *
 *   const handleClick = () => {
 *     toast({
 *       title: 'Success',
 *       description: 'Landing page created',
 *       variant: 'success',
 *     });
 *   };
 *
 *   return <button onClick={handleClick}>Create</button>;
 * }
 */
export function AppToastProvider({
  children,
  duration = 5000,
  swipeDirection = 'right',
  swipeThreshold = 50,
}: ToastProviderProps) {
  const toastState = useToastState();

  return (
    <ToastContext.Provider value={toastState}>
      <RadixToastProvider
        duration={duration}
        swipeDirection={swipeDirection}
        swipeThreshold={swipeThreshold}
      >
        {children}
        <Toaster />
      </RadixToastProvider>
    </ToastContext.Provider>
  );
}
